import { faCircleExclamation, faInbox, faRotate, type IconDefinition } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { QueueError } from "./QueueError"

import { cx } from "../utils"
import { hasPermission } from "../machines/AuthMachine"
import { Message } from "../routes/microservices-messages"
import { HorizontalBar } from "./QueueChart"

export const QueueDetail = ({ isMainPanel = false, queue, selected, setSelected, resetSelected }: QueueDetailProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const hasWritePermission = hasPermission("writeAccess")
  const missingInRabbitCount = queue.missingInRabbitCount ?? 0
  const missingInDbCount = queue.missingInDbCount ?? 0
  const divergenceCount = missingInRabbitCount + missingInDbCount
  const staleInProgressCount = queue.staleInProgressCount ?? 0
  const hasDivergence = queue.hasStateDivergence === true || divergenceCount > 0
  const hasStale = staleInProgressCount > 0
  const queueClass = (() => {
    const { consumers, messageStats, messages } = queue

    if (messages > 0 && consumers === 0) {
      return { border: "border-error", title: "bg-error text-error-content" }
    }

    if (hasDivergence || hasStale) {
      return {
        border: "border-warning",
        title: "bg-warning text-warning-content",
      }
    }
    // No consumers
    if (consumers === 0) {
      return { border: "border-info", title: "bg-info text-info-content" }
    }

    // Queue is in distress, we're failing at a rate of 80% of our delivery rate
    if (messages > 0 && messageStats.failureDetails.rate > messageStats.deliverDetails.rate * 0.7) {
      return {
        border: "border-neutral",
        title: "bg-neutral text-neutral-content",
      }
    }

    // We're publishing faster than we can deliver
    if (messageStats.publishDetails.rate > messageStats.deliverDetails.rate) {
      return {
        border: "border-warning",
        title: "bg-warning text-warning-content",
      }
    }

    // Delivering faster than we are publishing
    return {
      border: "border-success",
      title: "bg-success text-success-content",
    }
  })()

  useEffect(() => {
    if (selected && !isMainPanel && ref.current !== null) {
      const timerId = setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth" }), 150)

      return () => clearInterval(timerId)
    }
  }, [isMainPanel, selected])

  return (
    <div
      ref={ref}
      aria-expanded={selected}
      className={cx(
        "mx-auto flex w-full min-w-0 flex-col items-start justify-start rounded-lg border-[1px] bg-base-100 sm:mx-0",
        queueClass.border,
        [selected && isMainPanel, "h-full", "max-w-[235px]"],
      )}
    >
      <button
        type="button"
        title={selected ? "Close Expanded View" : "Open Expanded View"}
        onClick={() => {
          if (isMainPanel || selected) resetSelected()
          else setSelected?.()
        }}
        className={cx(
          "relative w-full p-2 text-center text-sm font-bold capitalize rounded-[inherit] rounded-b-none",
          queueClass.title,
          {
            "hover:brightness-90": !selected && !isMainPanel,
            "brightness-90 hover:brightness-95": selected && !isMainPanel,
          },
        )}
      >
        <span className="block text-center">{queue.name.replace(/([A-Z])/g, " $&").trim()}</span>
      </button>
      <div className="flex w-full grow flex-col px-3.5">
        <div className="flex h-24 w-full items-center justify-center">
          <HorizontalBar
            data={{
              labels: ["In p/s", "Out p/s", "Fail p/s"],
              datasets: [
                {
                  data: [
                    queue.messageStats.publishDetails.rate,
                    queue.messageStats.deliverDetails.rate,
                    queue.messageStats.failureDetails.rate,
                  ],
                },
              ],
            }}
          />
        </div>
        <div className="space-y-2 py-2">
          <QueueStat
            icon={faInbox}
            label="To Process"
            stat={queue.messages}
            link={hasWritePermission && `/microservices/messages?queueName=${queue.name}`}
          />
          <QueueStat
            icon={faRotate}
            label="In Progress"
            stat={queue.inProgress}
            link={hasWritePermission && `/microservices/processing?queueName=${queue.name}`}
          />
          {staleInProgressCount > 0 && (
            <QueueStat
              icon={faCircleExclamation}
              label="Stale In Progress"
              stat={staleInProgressCount}
              link={hasWritePermission && `/microservices/stale-processing?queueName=${queue.name}`}
            />
          )}
          <QueueStat
            label="Action Items"
            link={hasWritePermission && `/microservices/deadLetters?queueName=${queue.name}`}
            stat={queue.messageStats.failures}
          />
          {divergenceCount > 0 && (
            <QueueStat
              icon={faCircleExclamation}
              label="Divergence"
              stat={`${missingInRabbitCount}/${missingInDbCount}`}
              link={`/microservices/divergence?queueName=${queue.name}`}
            />
          )}

          <QueueStat label="Completed" stat={queue.messageStats.successes} />
          <QueueStat label="Per Message" stat={queue.timePerMessage} />
          <QueueStat label="To Complete" stat={queue.timeToComplete} />
          <QueueStat label="Workers" stat={queue.consumers} />
        </div>
        {hasWritePermission && selected && isMainPanel && (
          <>
            <hr className="border-gray-200 my-1.5 h-px border-t" />
            <QueueError queue={queue} />
          </>
        )}
      </div>
    </div>
  )
}

const QueueStat = ({ icon, link, stat, label }: QueueStatProps) => {
  return (
    <div className="text-black grid grid-cols-[.75rem,minmax(0,1fr),minmax(1rem,max-content)] gap-3">
      {icon != null && <FontAwesomeIcon icon={icon} />}
      <span className={cx("col-start-2 text-xs", { "font-semibold": icon != null })}>
        {typeof link === "string" ? (
          <Link to={link} className="link underline-offset-2">
            {label}
          </Link>
        ) : (
          label
        )}
      </span>
      <span className={cx("text-right text-xs", { "font-semibold": icon != null })}>{stat}</span>
    </div>
  )
}

interface QueueDetailProps {
  queue: Queue
  selected: boolean
  isMainPanel?: boolean
  setSelected?: () => void
  resetSelected: () => void
}

interface QueueStatProps {
  icon?: IconDefinition
  link?: string | false
  stat: number | string
  label: string
}

interface MessageStats {
  successes: number
  failures: number
  publishDetails: { rate: number }
  deliverDetails: { rate: number }
  failureDetails: { rate: number }
}

interface QueueArguments {
  maxPriority: number
}

export interface Queue {
  name: string
  consumers: number
  inProgress: number
  messages: number
  messageStats: MessageStats
  arguments: QueueArguments
  errorCounts: Array<{
    error: string
    count: number
  }>
  increment: string | null
  vHost: string
  timePerMessage: string
  timeToComplete: string
  items: Message[]
  hasStateDivergence?: boolean
  missingInRabbitCount?: number
  missingInDbCount?: number
  divergenceCheckedAtUtc?: string
  staleInProgressCount?: number
}
